import React, { useEffect, useRef, useState } from 'react';
import {
  Paper, Stack, Group, Text, Badge, TextInput, ActionIcon, ScrollArea, Loader, Switch, Divider, Code, Tooltip,
} from '@mantine/core';
import { IconSend, IconX, IconTool, IconSchool } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedPost from '../../hooks/useAuthenticatedPost';
import { useWorkspaceScope } from '../../workspace/WorkspaceScopeContext';
import TeachModal from '../teaching/TeachModal';
import config from '../../config';

/**
 * The Nymeria rail — chat + live tool stream. Read-only v1: it drives the workspace
 * (applies view directives to the shared WorkspaceScope) and answers questions. It
 * writes nothing. Rendered only while Nymeria is ON; unmounting it (toggle off) stops
 * everything. Nothing in Vesper depends on it.
 */
export default function NymeriaRail({ onOff }) {
  const navigate = useNavigate();
  const { scope, setWorklist } = useWorkspaceScope();
  const { post } = useAuthenticatedPost();

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: 'nymeria', text: "Hi — I'm your AI Sales Agent. Ask me to scope the board (\"what came in today?\"), find deals, or pull one up." },
  ]);
  const [tools, setTools] = useState([]);   // live tool stream
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [teach, setTeach] = useState(null);   // { context, idealOutput } for the Teach modal
  const scrollRef = useRef(null);
  const cancelRef = useRef(false);

  // The user prompt that immediately preceded message index i (for teaching context).
  const priorPrompt = (i) => {
    for (let j = i - 1; j >= 0; j -= 1) if (messages[j].role === 'user') return messages[j].text;
    return '';
  };

  // Start a session once, when the rail mounts (i.e. when turned on).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await post(`${config.apiBaseUrl}/api/nymeria/session`, {});
        if (alive) setSessionId(res?.sessionId ?? null);
      } catch { /* leave sessionId null; first turn will retry */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e6, behavior: 'smooth' }); }, [messages, busy]);

  const applyDirectives = (directives) => {
    (directives || []).forEach((d) => {
      if (d.tool === 'set_workspace_scope') {
        const p = d.patch || {};
        // All filters (incl. salesperson/minScore) live in the single worklist slice.
        if (p.worklist && Object.keys(p.worklist).length) setWorklist(p.worklist);
        if (p.view === 'cockpit') navigate('/sales/cockpit');
        else if (p.view === 'worklist') navigate('/sales/opportunities');
      } else if (d.tool === 'open_deal' && d.dealPublicId) {
        navigate(`/sales/cockpit?start=${encodeURIComponent(d.dealPublicId)}`);
      }
    });
  };

  const send = async () => {
    const prompt = input.trim();
    if (!prompt || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: prompt }]);
    setBusy(true); cancelRef.current = false;
    try {
      let sid = sessionId;
      if (!sid) { const s = await post(`${config.apiBaseUrl}/api/nymeria/session`, {}); sid = s?.sessionId; setSessionId(sid); }
      const res = await post(`${config.apiBaseUrl}/api/nymeria/turn`, { sessionId: sid, prompt, scope });
      if (cancelRef.current) return;   // rep hit stop; ignore the (completed) result
      setTools((t) => [...t, ...(res?.toolCalls || [])]);
      applyDirectives(res?.directives);
      setMessages((m) => [...m, { role: 'nymeria', text: res?.reply || '(no response)' }]);
    } catch (e) {
      if (!cancelRef.current) setMessages((m) => [...m, { role: 'error', text: `Error: ${e?.response?.data?.message || e.message}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper withBorder shadow="md" radius={0}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      {/* header */}
      <Group justify="space-between" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <Group gap={6}>
          <Text fw={700}>AI Sales Agent</Text>
          <Badge size="xs" variant="light" color="grape">assistant</Badge>
        </Group>
        <Group gap={8}>
          <Tooltip label="Turn the AI Sales Agent off"><Switch size="sm" checked onChange={() => onOff(false)} /></Tooltip>
          <ActionIcon variant="subtle" onClick={() => onOff(false)}><IconX size={16} /></ActionIcon>
        </Group>
      </Group>

      {/* chat */}
      <ScrollArea viewportRef={scrollRef} style={{ flex: 1 }} p="sm">
        <Stack gap="sm">
          {messages.map((m, i) => (
            <Paper key={i} withBorder={m.role !== 'user'} radius="md" p="xs"
              style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '90%',
                       background: m.role === 'user' ? 'var(--mantine-color-blue-light)' : undefined }}>
              <Text size="sm" c={m.role === 'error' ? 'red' : undefined} style={{ whiteSpace: 'pre-wrap' }}>{m.text}</Text>
              {m.role === 'nymeria' && i > 0 && (
                <Group justify="flex-end" mt={2}>
                  <Tooltip label="Teach — show the AI Sales Agent how you'd put it">
                    <ActionIcon size="xs" variant="subtle" color="grape"
                      onClick={() => setTeach({ context: priorPrompt(i), idealOutput: m.text })}>
                      <IconSchool size={13} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )}
            </Paper>
          ))}
          {busy && <Group gap="xs"><Loader size="xs" /><Text size="xs" c="dimmed">The AI Sales Agent is working…</Text></Group>}
        </Stack>
      </ScrollArea>

      {/* tool stream */}
      {tools.length > 0 && (
        <>
          <Divider label={<Group gap={4}><IconTool size={12} /><Text size="xs">tool stream ({tools.length})</Text></Group>} labelPosition="center" />
          <ScrollArea.Autosize mah={150} p="xs">
            <Stack gap={4}>
              {tools.slice(-12).map((t, i) => (
                <Group key={i} gap={6} wrap="nowrap" align="flex-start">
                  <Badge size="xs" variant="light" color={t.tier === 'write' ? 'orange' : 'teal'}>{t.tool}</Badge>
                  <Code style={{ fontSize: 10, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.input}</Code>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </>
      )}

      {/* input */}
      <Group gap={6} p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <TextInput
          style={{ flex: 1 }} size="sm" placeholder="Ask the AI Sales Agent…"
          value={input} onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          disabled={busy}
        />
        {busy
          ? <Tooltip label="Stop watching (server finishes + logs)"><ActionIcon color="red" variant="light" onClick={() => { cancelRef.current = true; setBusy(false); }}><IconX size={16} /></ActionIcon></Tooltip>
          : <ActionIcon color="grape" variant="filled" onClick={send} disabled={!input.trim()}><IconSend size={16} /></ActionIcon>}
      </Group>

      <TeachModal
        opened={!!teach}
        onClose={() => setTeach(null)}
        surface="nymeria"
        context={teach?.context || ''}
        idealOutput={teach?.idealOutput || ''}
        onSaved={() => setTeach(null)}
      />
    </Paper>
  );
}
